import { Check } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { ProfileSection } from '../../../../components/profile/ProfileSection';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateOnly } from '../../../../lib/formatDate';
import {
  DISCIPLINARY_ACTION_TYPE_LABELS,
  DISCIPLINARY_CATEGORY_LABELS,
  DISCIPLINARY_STATUS_LABELS,
  type PersonnelDetail,
  type PersonnelDisciplinaryRecord,
} from '../personnelTypes';

/**
 * Disciplinary and administrative records — sanctions imposed, and matters raised.
 *
 * **Captured from the hospital system on 18 Sep 2026**, and filed after SALN: like that one it
 * is something the 201 file HOLDS rather than a section CS Form 212 asks for, so it appends
 * after the form's own run, inside the `201 File` group.
 *
 * **MOST RECENT INCIDENT first, and undated rows LAST.** A matter is a moment, so the newest is
 * what a reader is looking for — and an incident nobody dated is not a recent one, so it sinks
 * rather than floating to the top where it would read as the newest thing on the file. The same
 * call the Recognition and L&D panels take.
 *
 * **The CASE STATUS is not the row's state, and this panel has to show both.** `Status` is where
 * the matter got to — a dismissed case is one where the charge did not stand, which stays on the
 * file — while the row's own `isActive` is whether the record is shown at all. They are two
 * columns, deliberately, because collapsing them would make "dismissed" ambiguous between an
 * outcome and a deletion.
 */
export function DisciplinaryPanel({
  record,
  onAdd,
  onEdit,
  onDelete,
  onBulkDelete,
  onRestore,
}: {
  record: PersonnelDetail;
  onAdd: () => void;
  onEdit: (row: PersonnelDisciplinaryRecord) => void;
  onDelete: (row: PersonnelDisciplinaryRecord) => void;
  onBulkDelete: (rows: PersonnelDisciplinaryRecord[]) => void;
  onRestore: (row: PersonnelDisciplinaryRecord) => void;
}) {

  const [showDeleted, setShowDeleted] = useState(false);

  const rows = useMemo(
    () =>
      showDeleted
        ? record.disciplinaryRecords
        : record.disciplinaryRecords.filter((x) => x.isActive),
    [record.disciplinaryRecords, showDeleted],
  );

  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      const matched = term
        ? rows.filter((x) =>
            [
              x.subject,
              x.caseNumber ?? '',
              x.description ?? '',
              x.actionTaken ?? '',
              // A visible column the search cannot see is a filter that answers less than the
              // table shows. `remarks` became a column in the same change that added this.
              x.remarks ?? '',
              DISCIPLINARY_ACTION_TYPE_LABELS[x.type],
              DISCIPLINARY_STATUS_LABELS[x.status],
            ].some((value) => value.toLowerCase().includes(term)),
          )
        : rows;

      const field = query.sort?.replace(/^-/, '') ?? 'incidentDate';
      const descending = query.sort?.startsWith('-') ?? false;
      const direction = descending ? -1 : 1;

      const sorted = [...matched].sort((a, b) => {
        if (field === 'incidentDate' || field === 'dateFiled' || field === 'resolutionDate') {
          /*
            Undated rows sort LAST whichever way the column points — a date nobody wrote down is
            not a recent one. Compared as STRINGS, which is safe precisely because these are
            `YYYY-MM-DD` calendar dates: they sort lexicographically in date order, and parsing
            them into `Date` would put a calendar date through the instant path.
          */
          const av = (a[field] as string | null | undefined) ?? null;
          const bv = (b[field] as string | null | undefined) ?? null;
          if (av === null && bv === null) return a.subject.localeCompare(b.subject);
          if (av === null) return 1;
          if (bv === null) return -1;
          const gap = av.localeCompare(bv) * direction;
          return gap !== 0 ? gap : a.subject.localeCompare(b.subject);
        }

        const pick = (row: PersonnelDisciplinaryRecord) =>
          field === 'type'
            ? DISCIPLINARY_ACTION_TYPE_LABELS[row.type]
            : field === 'category'
              ? DISCIPLINARY_CATEGORY_LABELS[row.category]
              : field === 'status'
                ? DISCIPLINARY_STATUS_LABELS[row.status]
                : field === 'caseNumber'
                  ? (row.caseNumber ?? '')
                  : row.subject;

        return String(pick(a)).localeCompare(String(pick(b))) * direction;
      });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
      });
    },
    [rows],
  );

  const state = useDataTable<PersonnelDisciplinaryRecord>({
    fetchPage,
    pageSize: 10,

    // Most recent first, matching the order the server sends them in — or the profile and its
    // own table would disagree about which row is first.
    defaultSort: '-incidentDate',
  });

  const columns: DataColumn<PersonnelDisciplinaryRecord>[] = [
    {
      /*
        The ANCHOR is the SUBJECT — what the matter was about, which is what identifies the row.
        Not the type, which titles four rows "Written warning"; not the case number, which is
        quoted from somebody else's document and is often absent. The mistake the family and
        education panels each made first.
      */
      key: 'subject',
      header: 'Subject',
      width: 280,
      sortable: true,
      render: (x) => <TextCell value={x.subject} />,
    },
    {
      key: 'type',
      header: 'Type',
      width: 170,
      sortable: true,
      summary: true,
      render: (x) => <TextCell value={DISCIPLINARY_ACTION_TYPE_LABELS[x.type]} />,
    },
    {
      /*
        DERIVED server-side from the type. It reads as a duplicate of the column beside it until
        you have thirty rows and want the sanctions separated from the matters raised — which is
        the one question this section is scanned for that the type alone answers slowly.
      */
      key: 'category',
      header: 'Category',
      width: 140,
      sortable: true,
      render: (x) => <TextCell value={DISCIPLINARY_CATEGORY_LABELS[x.category]} />,
    },
    {
      key: 'caseNumber',
      header: 'Case number',
      width: 150,
      sortable: true,
      summary: true,
      // A code, so mono — and quoted from a document this system did not issue.
      render: (x) => <TextCell value={x.caseNumber ?? null} mono />,
    },
    {
      key: 'incidentDate',
      header: 'Incident',
      width: 130,
      sortable: true,
      summary: true,
      /*
        `formatDateOnly` through a `<TextCell mono />`, never `<DateCell>`: these are CALENDAR
        dates and `<DateCell>` renders an instant with a time. Put through the instant path, an
        incident dated 01 January reads as 31 December for a reader west of UTC.
      */
      render: (x) => <TextCell value={x.incidentDate ? formatDateOnly(x.incidentDate) : null} mono />,
    },
    {
      key: 'dateFiled',
      header: 'Filed',
      width: 130,
      sortable: true,
      render: (x) => <TextCell value={x.dateFiled ? formatDateOnly(x.dateFiled) : null} mono />,
    },
    {
      /*
        THE CASE's status, which is not the ROW's state — see the panel's own note. It is a
        plain column rather than the status pill, because `status: true` is the flag that marks
        the row's live/deleted state and the shell hoists that column to the end. Two pills
        would read as two answers to one question.
      */
      key: 'status',
      header: 'Case status',
      width: 160,
      sortable: true,
      summary: true,
      render: (x) => <TextCell value={DISCIPLINARY_STATUS_LABELS[x.status]} />,
    },
    {
      key: 'resolutionDate',
      header: 'Resolved',
      width: 130,
      sortable: true,
      render: (x) => (
        <TextCell value={x.resolutionDate ? formatDateOnly(x.resolutionDate) : null} mono />
      ),
    },
    {
      key: 'actionTaken',
      header: 'Action taken',
      width: 260,
      render: (x) => <TextCell value={x.actionTaken ?? null} />,
    },
    {
      /*
        THE NARRATIVE and the note under it. Both are collected by the form and neither was
        rendered anywhere: no column, and therefore no row in the preview either, which is
        built from these same columns. A disciplinary record whose description is the whole
        content of it could be written and never read back.

        They sit AFTER `Action taken`, the other free-text field, so the three narrative
        columns run together at the end of the table rather than splitting the dates.
      */
      key: 'description',
      header: 'Description',
      width: 300,
      render: (x) => <TextCell value={x.description ?? null} />,
    },
    {
      key: 'remarks',
      header: 'Remarks',
      width: 260,
      render: (x) => <TextCell value={x.remarks ?? null} />,
    },
    {
      /*
        A panel whose rows can be deleted owes a status column: without one a deleted row carries
        no marker while Show deleted is on, which is the one state a row must never be silent
        about. `<DataTablePanel>` moves it to the end of the data columns itself.
      */
      key: 'rowStatus',
      header: 'Status',
      width: 110,
      status: true,
      render: (x) =>
        x.isActive ? (
          <StatusText tone="active">Active</StatusText>
        ) : (
          <StatusText tone="inactive">Deleted</StatusText>
        ),
    },
  ];

  return (
    <ProfileSection>
      <DataTablePanel<PersonnelDisciplinaryRecord>
        id="personnel-disciplinary"
        state={state}
        // The size of the modal that EDITS this record, so reading a row and correcting it
        // happen at one width rather than the dialog changing shape between them.
        rowPreview={{ title: 'Disciplinary record', size: '2xl' }}
        columns={columns}
        rowKey={(x) => x.id}
        searchPlaceholder="Search subject, case number, type or status"
        onCreate={onAdd}
        createPermission="personnel.update"
        createDisabledReason={
          record.isActive
            ? null
            : 'This employee is deleted. Restore them before editing the record.'
        }
        rowActions={(x) => [
          {
            key: `edit-${x.id}`,
            label: 'Edit',
            permission: 'personnel.update',
            disabledReason: x.isActive
              ? null
              : 'This record is deleted. Restore it before editing.',
            onClick: () => onEdit(x),
          },
          x.isActive
            ? {
                key: `delete-${x.id}`,
                label: 'Delete',
                permission: 'personnel.update',
                destructive: true,
                onClick: () => onDelete(x),
              }
            : {
                key: `restore-${x.id}`,
                label: 'Restore',
                permission: 'personnel.update',
                onClick: () => onRestore(x),
              },
        ]}
        otherInfo={(x) => ({
          createdAt: x.createdAt,
          createdByName: x.createdByName,
          updatedAt: x.updatedAt,
          updatedByName: x.updatedByName,
        })}
        bulkActions={(selected) => {
          const deletable = selected.filter((x) => x.isActive);

          return [
            {
              key: 'delete-disciplinary',
              label: `Delete ${deletable.length} record${deletable.length === 1 ? '' : 's'}`,
              permission: 'personnel.update',
              destructive: true,
              disabledReason: !record.isActive
                ? 'This employee is deleted. Restore them before editing the record.'
                : deletable.length === 0
                  ? 'Every selected record is already deleted.'
                  : null,
              onClick: () => onBulkDelete(deletable),
            },
          ];
        }}
        settingsItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            icon: showDeleted ? <Check className="h-4 w-4" aria-hidden="true" /> : undefined,
            onClick: () => setShowDeleted((current) => !current),
          },
        ]}
        emptyMessage="No disciplinary or administrative records."
        emptySearchMessage="No record matches that search."
      />
    </ProfileSection>
  );
}
